<template>
  <div class="shop-products-page">
    <div v-if="loading" class="loading">加载中...</div>
    
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-if="!loading && seller" class="shop-header">
      <h1>{{ seller.shopName }}</h1>
      <p class="seller-info">店铺名称：{{ seller.shopName }}</p>
    </div>

    <div class="products-container" v-if="!loading && products.length > 0">
      <div class="product-list">
        <div 
          class="product-card" 
          v-for="product in products" 
          :key="product.id"
          @mouseenter="hoveredProductId = product.id"
          @mouseleave="hoveredProductId = null"
        >
          <router-link :to="{ name: 'productDetail', params: { id: product.id } }" class="product-image">
            <img
              :src="getProductImageUrl(product)"
              :alt="product.name || '商品图片'"
              class="product-img"
              @error="handleImageError($event, product.id)"
            />
          </router-link>
          
          <div class="product-info">
            <h3 class="product-name">{{ product.name }}</h3>
            <div class="product-price">¥{{ product.price.toFixed(2) }}</div>
          </div>
          
          <div class="product-actions" :class="{ 'show': hoveredProductId === product.id }">
            <button 
              class="action-btn buy-btn" 
              @click="handleBuyClick(product)"
              :disabled="!canBuy || product.stock <= 0"
            >
              购买
            </button>
            <button 
              class="action-btn cart-btn" 
              @click="addToCart(product)"
              :disabled="!canBuy || product.stock <= 0"
            >
              加入购物车
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && products.length === 0" class="alert alert-info">
      该店铺暂无在售商品
    </div>

    <!-- 尺码选择弹窗 -->
    <div v-if="showBuyForm && currentProduct" class="modal-overlay">
      <div class="size-selector-modal">
        <div class="modal-header">
          <h2>选择尺码</h2>
          <button class="close-btn" @click="closeBuyForm">×</button>
        </div>
        
        <div v-if="loadingSizeDetails" class="loading-container small">
          <div class="spinner"></div>
          <p>加载尺码信息中...</p>
        </div>
        <div v-else-if="sizeDetails.length > 0">
          <div class="size-options">
            <div 
              v-for="sizeDetail in sizeDetails" 
              :key="sizeDetail.id"
              class="size-option"
              :class="{ 
                'selected': selectedSizeDetail?.id === sizeDetail.id,
                'out-of-stock': sizeDetail.stock <= 0
              }"
              @click="selectSize(sizeDetail)"
            >
              <span class="size-code">{{ sizeDetail.size.code }}</span>
              <span class="size-name">{{ sizeDetail.size.name }}</span>
              <span class="size-stock">
                {{ sizeDetail.stock > 0 ? `库存: ${sizeDetail.stock}` : '无货' }}
              </span>
              <span v-if="sizeDetail.extraPrice > 0" class="size-extra-price">
                +¥{{ sizeDetail.extraPrice.toFixed(2) }}
              </span>
            </div>
          </div>
          
          <div v-if="selectedSizeDetail" class="selected-size-info">
            <p>已选择: {{ selectedSizeDetail.size.code }} - {{ selectedSizeDetail.size.name }}</p>
            <p v-if="selectedSizeDetail.extraPrice > 0">
              附加价格: ¥{{ selectedSizeDetail.extraPrice.toFixed(2) }}
            </p>
          </div>
          
          <div class="quantity-selector">
            <label>购买数量:</label>
            <div class="quantity-controls">
              <button class="quantity-btn" @click="decreaseQuantity" :disabled="quantity <= 1">-</button>
              <input type="number" v-model.number="quantity" min="1" :max="selectedSizeDetail?.stock || currentProduct.stock">
              <button class="quantity-btn" @click="increaseQuantity" :disabled="quantity >= (selectedSizeDetail?.stock || currentProduct.stock)">+</button>
            </div>
          </div>
          
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeBuyForm">取消</button>
            <button 
              class="btn btn-primary" 
              @click="confirmSizeAndBuy"
              :disabled="!selectedSizeDetail || selectedSizeDetail.stock <= 0"
            >
              确认购买
            </button>
          </div>
        </div>
        <div v-else>
          <p class="no-size-info">该商品无需选择尺码</p>
          <div class="quantity-selector">
            <label>购买数量:</label>
            <div class="quantity-controls">
              <button class="quantity-btn" @click="decreaseQuantity" :disabled="quantity <= 1">-</button>
              <input type="number" v-model.number="quantity" min="1" :max="currentProduct.stock">
              <button class="quantity-btn" @click="increaseQuantity" :disabled="quantity >= currentProduct.stock">+</button>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeBuyForm">取消</button>
            <button class="btn btn-primary" @click="confirmSizeAndBuy">确认购买</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 购买表单弹窗 - 使用统一CheckoutForm组件 -->
    <div v-if="showCheckoutForm" class="modal-overlay" @click.self="closeCheckoutForm">
      <div class="checkout-modal unified-checkout">
        <div class="modal-header">
          <h2>确认订单</h2>
          <button class="close-btn" @click="closeCheckoutForm">×</button>
        </div>
        <CheckoutForm 
          :items="checkoutItems" 
          @close="closeCheckoutForm"
          @order-created="onOrderCreated"
          @payment-success="onPaymentSuccess"
        />
      </div>
    </div>

    <!-- 支付成功弹窗 -->
    <div v-if="buySuccess" class="modal-overlay">
      <div class="success-modal">
        <div class="success-icon">✓</div>
        <h3>支付成功</h3>
        <p>订单 #{{ createdOrderId }} 支付成功</p>
        <div class="success-actions">
          <button class="btn btn-secondary" @click="viewOrderDetail">查看订单</button>
          <button class="btn btn-primary" @click="continueShopping">继续购物</button>
        </div>
      </div>
    </div>
    
    <!-- 统一非模态Toast提示 -->
    <div class="toast-container">
      <div 
        v-for="toast in toasts" 
        :key="toast.id" 
        class="toast"
        :class="toast.type"
      >
        <span class="toast-icon">{{ getToastIcon(toast.type) }}</span>
        <span class="toast-message">{{ toast.message }}</span>
        <button class="toast-close" @click="removeToast(toast.id)">×</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CheckoutForm from '@/components/CheckoutForm.vue';

export default {
  name: 'ShopProducts',
  components: { CheckoutForm },
  data() {
    return {
      products: [],
      seller: null,
      loading: true,
      error: null,
      hoveredProductId: null,
      showBuyForm: false,
      showCheckoutForm: false,
      currentProduct: null,
      buyer: { notes: '' },
      quantity: 1,
      // 尺码相关
      sizeDetails: [],
      selectedSizeDetail: null,
      loadingSizeDetails: false,
      buySuccess: false,
      submitting: false,
      toasts: [],
      createdOrderId: null
    };
  },
  computed: {
    isBuyerLoggedIn() {
      return !!localStorage.getItem('customerToken');
    },
    canBuy() {
      return this.isBuyerLoggedIn;
    },
    // 购买商品列表（用于CheckoutForm）
    checkoutItems() {
      if (!this.currentProduct) return [];
      return [{
        id: this.currentProduct.id,
        productId: this.currentProduct.id,
        productName: this.currentProduct.name,
        price: this.currentProduct.price,
        quantity: this.quantity,
        imageUrl: this.getProductImageUrl(this.currentProduct),
        sizeId: this.selectedSizeDetail?.id || null,
        sizeName: this.selectedSizeDetail?.size?.name || null,
        sizeExtraPrice: this.selectedSizeDetail?.extraPrice || 0
      }];
    }
  },
  created() {
    const sellerId = this.$route.params.sellerId;
    if (sellerId) {
      this.fetchSellerAndProducts(sellerId);
    } else {
      this.error = '无效的店铺ID';
      this.loading = false;
    }
  },
  methods: {
    async fetchSellerAndProducts(sellerId) {
      try {
        this.loading = true;
        
        const [sellerResponse, productsResponse] = await Promise.all([
          axios.get(`/seller/${sellerId}`),
          axios.get(`/products/seller/${sellerId}/active`)
        ]);
        
        this.seller = sellerResponse.data;
        const productsData = productsResponse.data.data || productsResponse.data;
        this.products = Array.isArray(productsData) ? productsData : [];
        
        this.error = '';
      } catch (error) {
        console.error('获取店铺信息失败:', error);
        this.error = '获取店铺信息失败，请稍后重试。';
        this.products = [];
      } finally {
        this.loading = false;
      }
    },
    getProductImageUrl(product) {
      if (!product.imageUrl) {
        return 'https://picsum.photos/seed/product-' + product.id + '/300/300';
      }
      
      if (!product.imageUrl.startsWith('http')) {
        return '/api/files/images/' + product.imageUrl;
      }
      
      if (product.imageUrl.includes('huaban.com')) {
        return 'https://picsum.photos/seed/product-' + product.id + '/300/300';
      }
      
      return product.imageUrl;
    },
    handleImageError(event, productId) {
      const defaultImage = 'https://picsum.photos/seed/product-' + productId + '/300/300';
      if (event.target.src !== defaultImage) {
        event.target.src = defaultImage;
      }
    },
    async handleBuyClick(product) {
      if (!this.isBuyerLoggedIn) {
        this.showError('请先登录');
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
      } else {
        this.currentProduct = product;
        this.quantity = 1;
        this.selectedSizeDetail = null;
        await this.fetchSizeDetails(product.id);
        this.showBuyForm = true;
      }
    },
    async fetchSizeDetails(productId) {
      this.loadingSizeDetails = true;
      try {
        const response = await this.$axios.get(`/products/${productId}/sizes`);
        this.sizeDetails = response.data.data || response.data || [];
      } catch (error) {
        console.error('获取尺码详情失败:', error);
        this.sizeDetails = [];
      } finally {
        this.loadingSizeDetails = false;
      }
    },

    selectSize(sizeDetail) {
      if (sizeDetail.stock > 0) {
        this.selectedSizeDetail = sizeDetail;
      }
    },

    increaseQuantity() {
      const maxStock = this.selectedSizeDetail ? this.selectedSizeDetail.stock : this.currentProduct.stock;
      if (this.quantity < maxStock) {
        this.quantity++;
      }
    },

    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },

    closeBuyForm() {
      this.showBuyForm = false;
      this.selectedSizeDetail = null;
      this.quantity = 1;
    },

    confirmSizeAndBuy() {
      if (this.sizeDetails.length > 0 && !this.selectedSizeDetail) {
        alert('请选择尺码');
        return;
      }
      this.showBuyForm = false;
      this.showCheckoutForm = true;
    },

    closeCheckoutForm() {
      this.showCheckoutForm = false;
      this.selectedSizeDetail = null;
      this.quantity = 1;
    },

    onOrderCreated(orderId) {
      this.createdOrderId = orderId;
      this.showToast('订单创建成功', 'success');
    },

    onPaymentSuccess() {
      this.showToast('支付成功', 'success');
    },

    viewOrderDetail() {
      this.buySuccess = false;
      this.$router.push('/orders');
    },

    continueShopping() {
      this.buySuccess = false;
      this.createdOrderId = null;
      this.currentProduct = null;
    },
    
    // 统一Toast提示方法
    showToast(message, type = 'info', duration = 3000) {
      const toast = {
        id: Date.now() + Math.random(),
        message,
        type
      };
      this.toasts.push(toast);
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(toast.id);
        }, duration);
      }
    },
    
    showError(msg) {
      this.showToast(msg, 'error');
    },
    
    showSuccess(msg) {
      this.showToast(msg, 'success');
    },
    
    showInfo(msg) {
      this.showToast(msg, 'info');
    },
    
    showWarning(msg) {
      this.showToast(msg, 'warning');
    },
    
    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id);
      if (index !== -1) {
        this.toasts.splice(index, 1);
      }
    },
    
    getToastIcon(type) {
      const icons = {
        success: '✓',
        error: '✕',
        info: 'ℹ',
        warning: '!'
      };
      return icons[type] || 'ℹ';
    },

    async addToCart(product) {
      if (!this.isBuyerLoggedIn) {
        this.showError('请先登录');
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
        return;
      }
      
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        const response = await this.$axios.post('/cart/add', {
          productId: product.id,
          quantity: 1
        }, {
          headers: { 'X-Username': encodedUsername }
        });
        
        if (response.data.success) {
          this.showSuccess(response.data.message);
        } else {
          this.showError('错误: ' + response.data.message);
        }
      } catch (error) {
        this.showError('加入购物车失败: ' + (error.response?.data?.message || '请稍后重试'));
      }
    }
  }
};
</script>

<style scoped>
.shop-products-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.loading {
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: #666;
}

.shop-header {
  margin-bottom: 30px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.shop-header h1 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 24px;
}

.seller-info {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.products-container {
  margin-top: 20px;
}

.product-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  width: 100%;
}

@media (max-width: 1200px) {
  .product-list {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .product-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .product-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .product-list {
    grid-template-columns: 1fr;
  }
}

.product-card {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
}

.product-card:hover {
  transform: scale(1.03);
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
}

.product-image {
  display: block;
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  position: relative;
  overflow: hidden;
}

.product-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-img {
  transform: scale(1.05);
}

.product-info {
  padding: 12px;
}

.product-name {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  line-height: 1.4;
  height: 2.8em;
}

.product-price {
  font-size: 19px;
  font-weight: bold;
  color: #333;
  margin: 0;
}

.product-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.product-actions.show {
  transform: translateY(0);
}

.action-btn {
  flex: 1;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.buy-btn {
  background-color: #E53935;
  color: white;
}

.buy-btn:hover:not(:disabled) {
  background-color: #C62828;
}

.cart-btn {
  background-color: #FF7043;
  color: white;
}

.cart-btn:hover:not(:disabled) {
  background-color: #F4511E;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  z-index: 1001;
}

.buy-form {
  background-color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  min-width: 400px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  background-color: #E02E24;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn:hover {
  background-color: #c81623;
}

.btn-secondary {
  background-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.address-section {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
}

.selected-address {
  background-color: white;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.address-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.receiver-name {
  font-weight: bold;
}

.phone {
  color: #666;
}

.default-badge {
  background-color: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.address-detail {
  font-size: 13px;
  color: #555;
}

.no-address {
  text-align: center;
  padding: 20px;
  color: #666;
}

.address-selector {
  width: 90%;
  max-width: 500px;
  background-color: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
}

.address-list {
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: 15px;
}

.address-option {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.address-option:hover {
  border-color: #007bff;
}

.address-option.selected {
  border-color: #007bff;
  background-color: #e7f3ff;
}

.selector-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.success-modal {
  background-color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  text-align: center;
}

.success-modal h3 {
  margin: 0 0 10px 0;
  color: #28a745;
}

.success-modal p {
  margin: 0 0 20px 0;
  color: #666;
}

.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 20px 30px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1001;
  font-size: 14px;
  text-align: center;
  max-width: 350px;
  width: 90%;
}

.toast.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.toast.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.alert {
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.alert-info {
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

/* 步骤进度条样式 */
.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 20px;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ddd;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.3s ease;
}

.step.active .step-circle {
  background-color: #ff6600;
  color: white;
}

.step.completed .step-circle {
  background-color: #ddd;
  color: #999;
}

.step-label {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.step.active .step-label {
  color: #333;
  font-weight: 500;
}

.step-line {
  width: 80px;
  height: 3px;
  background-color: #ddd;
}

.step-line.active {
  background-color: #ddd;
}

/* 统一checkout-modal样式 */
.checkout-modal {
  background-color: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.checkout-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.checkout-modal .modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.checkout-modal .close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background-color: #f5f5f5;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: all 0.2s ease;
}

.checkout-modal .close-btn:hover {
  background-color: #e0e0e0;
  color: #333;
}

.checkout-modal .modal-body {
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(90vh - 180px);
}

.checkout-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  position: sticky;
  bottom: 0;
  background-color: white;
}

/* 按钮样式 */
.checkout-modal .btn {
  padding: 12px 24px;
  font-size: 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.checkout-modal .btn-primary {
  background-color: #ff6600;
  color: white;
}

.checkout-modal .btn-primary:hover {
  background-color: #e65c00;
}

.checkout-modal .btn-secondary {
  background-color: #f5f5f5;
  color: #666;
  flex: 1;
}

.checkout-modal .btn-secondary:hover {
  background-color: #e8e8e8;
}

.checkout-modal .btn-confirm {
  background-color: #e53935;
  color: white;
  flex: 1;
  font-weight: bold;
}

.checkout-modal .btn-confirm:hover:not(:disabled) {
  background-color: #c62828;
}

.checkout-modal .btn-confirm:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.checkout-modal .btn-outline {
  background-color: transparent;
  border: 1px solid #ddd;
  color: #666;
}

.checkout-modal .btn-outline:hover {
  border-color: #ff6600;
  color: #ff6600;
}

.checkout-modal .btn-sm {
  padding: 6px 16px;
  font-size: 12px;
}

/* 区域标题 */
.checkout-modal .section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.checkout-modal .title-icon {
  font-size: 18px;
}

.checkout-modal .section-title h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

/* 地址区域 */
.checkout-modal .address-section {
  margin-bottom: 24px;
}

.checkout-modal .address-card {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.checkout-modal .address-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.checkout-modal .receiver-name {
  font-size: 16px;
  font-weight: bold;
}

.checkout-modal .phone {
  font-size: 14px;
  color: #666;
}

.checkout-modal .default-tag {
  font-size: 12px;
  color: white;
  background-color: #ff6600;
  padding: 2px 8px;
  border-radius: 4px;
}

.checkout-modal .address-detail {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.checkout-modal .no-address {
  text-align: center;
  padding: 24px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.checkout-modal .no-address p {
  margin-bottom: 16px;
  color: #999;
}

/* 商品清单 */
.checkout-modal .products-section {
  margin-bottom: 24px;
}

.checkout-modal .order-products {
  background-color: #fafafa;
  border-radius: 8px;
  overflow: hidden;
}

.checkout-modal .order-product {
  display: flex;
  align-items: center;
  padding: 12px;
}

.checkout-modal .product-img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 12px;
  flex-shrink: 0;
}

.checkout-modal .product-info {
  flex: 1;
  min-width: 0;
}

.checkout-modal .product-info .product-name {
  font-size: 14px;
  color: #333;
  margin: 0 0 6px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checkout-modal .product-info .product-price {
  font-size: 14px;
  color: #ff6600;
  margin: 0;
}

.checkout-modal .product-qty {
  font-size: 14px;
  color: #999;
  margin-right: 8px;
}

.checkout-modal .product-total {
  font-size: 16px;
  font-weight: bold;
  color: #ff6600;
}

/* 金额区域 */
.checkout-modal .amount-section {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 24px;
}

.checkout-modal .amount-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: #666;
}

.checkout-modal .amount-row.total {
  font-weight: bold;
  font-size: 18px;
  color: #ff6600;
  padding-top: 12px;
  margin-top: 8px;
}

/* 备注区域 */
.checkout-modal .notes-section {
  margin-bottom: 16px;
}

.checkout-modal .notes-section label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.checkout-modal .notes-section input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
}

.checkout-modal .notes-section input:focus {
  border-color: #ff6600;
}

/* 地址选择器弹窗 */
.address-modal {
  max-width: 500px;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
}

.address-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.address-modal .modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.address-modal .close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background-color: #f5f5f5;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}

.address-modal .close-btn:hover {
  background-color: #e0e0e0;
  color: #333;
}

.address-modal .address-list {
  padding: 0 24px;
  max-height: 400px;
  overflow-y: auto;
}

.address-modal .address-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 2px solid transparent;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.address-modal .address-option:hover {
  border-color: #ff4400;
  background-color: #fff8f5;
}

.address-modal .address-option.selected {
  border-color: #ff4400;
  background-color: #fff8f5;
}

.address-modal .address-radio input {
  display: none;
}

.address-modal .address-radio label {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  margin-top: 2px;
}

.address-modal .address-option.selected .address-radio label {
  border-color: #ff4400;
}

.address-modal .address-option.selected .address-radio label::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background-color: #ff4400;
  border-radius: 50%;
}

.address-modal .address-content {
  flex: 1;
}

.address-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
}

.address-modal .btn {
  padding: 10px 24px;
  font-size: 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
}

.address-modal .btn-primary {
  background-color: #ff4400;
  color: white;
}

.address-modal .btn-secondary {
  background-color: #f5f5f5;
  color: #666;
}

/* 支付弹窗 */
.payment-modal {
  max-width: 450px;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
}

.payment-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.payment-modal .modal-header h3 {
  margin: 0;
}

.payment-modal .close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background-color: #f5f5f5;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  color: #999;
}

.payment-modal .close-btn:hover {
  background-color: #e0e0e0;
  color: #333;
}

.payment-modal .modal-body {
  padding: 20px;
}

.payment-modal .payment-info {
  text-align: center;
  padding: 24px;
  background-color: #fff5f5;
  border-radius: 8px;
  margin-bottom: 20px;
}

.payment-modal .payment-info p {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #666;
}

.payment-modal .payment-info .total-amount {
  font-size: 28px;
  font-weight: bold;
  color: #ff4400;
  margin-top: 16px !important;
}

.payment-modal .payment-methods {
  padding: 0;
}

.payment-modal .method-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.payment-modal .method-item:hover {
  border-color: #ff4400;
}

.payment-modal .method-item input {
  width: 20px;
  height: 20px;
}

.payment-modal .method-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.payment-modal .modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
}

.payment-modal .btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
}

.payment-modal .btn-secondary {
  background-color: #f5f5f5;
  color: #666;
}

.payment-modal .btn-primary {
  background-color: #ff4400;
  color: white;
}

/* 支付成功弹窗 */
.success-modal {
  max-width: 400px;
  text-align: center;
  padding: 40px 24px;
  background-color: white;
  border-radius: 12px;
}

.success-modal .success-icon {
  width: 80px;
  height: 80px;
  background-color: #28a745;
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 40px;
  font-weight: bold;
  margin: 0 auto 20px;
}

.success-modal h3 {
  font-size: 24px;
  font-weight: 600;
  color: #28a745;
  margin: 0 0 10px 0;
}

.success-modal p {
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
}

.success-modal .success-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.success-modal .btn {
  padding: 10px 24px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.success-modal .btn-secondary {
  background-color: #f5f5f5;
  color: #666;
}

.success-modal .btn-primary {
  background-color: #ff4400;
  color: white;
}

/* Toast提示样式 */
.toast-container {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toast {
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 200px;
  max-width: 350px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.toast-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.toast-message {
  flex: 1;
  word-break: break-all;
}

.toast-close {
  width: 20px;
  height: 20px;
  border: none;
  background-color: transparent;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.6;
}

.toast-close:hover {
  opacity: 1;
}

.toast.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.toast.success .toast-icon {
  background-color: #22c55e;
  color: white;
}

.toast.error {
  background-color: #fee2e2;
  color: #dc2626;
}

.toast.error .toast-icon {
  background-color: #ef4444;
  color: white;
}

.toast.warning {
  background-color: #fef3c7;
  color: #d97706;
}

.toast.warning .toast-icon {
  background-color: #f59e0b;
  color: white;
}

.toast.info {
  background-color: #dbeafe;
  color: #2563eb;
}

.toast.info .toast-icon {
  background-color: #3b82f6;
  color: white;
}

/* 尺码选择弹窗样式 */
.size-selector-modal {
  max-width: 500px;
  margin: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.size-selector-modal .modal-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.size-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  padding: 15px 20px;
  margin-bottom: 15px;
}

.size-option {
  padding: 12px 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background-color: white;
}

.size-option:hover:not(.out-of-stock) {
  border-color: #ff6b6b;
  background-color: #fff5f5;
}

.size-option.selected {
  border-color: #ff6b6b;
  background-color: #fff5f5;
  box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.2);
}

.size-option.out-of-stock {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.size-code {
  font-weight: bold;
  font-size: 16px;
  color: #333;
}

.size-name {
  font-size: 14px;
  color: #666;
}

.size-stock {
  font-size: 12px;
  color: #999;
}

.size-extra-price {
  font-size: 12px;
  color: #ff6b6b;
  font-weight: bold;
}

.selected-size-info {
  padding: 12px 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 15px;
}

.selected-size-info p {
  margin: 5px 0;
  font-size: 14px;
  color: #333;
}

.quantity-selector {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.quantity-selector label {
  font-weight: 500;
  color: #333;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quantity-controls input[type="number"] {
  width: 60px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}

.quantity-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.quantity-btn:hover:not(:disabled) {
  background-color: #ff6b6b;
  color: white;
  border-color: #ff6b6b;
}

.quantity-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.no-size-info {
  text-align: center;
  padding: 30px 20px;
  color: #666;
  font-size: 14px;
}

/* 统一CheckoutForm样式 */
.unified-checkout {
  max-height: 85vh;
}

.unified-checkout .checkout-form {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}
</style>